import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalesforce } from '../hooks/useSalesforce'
import { useWorkspace } from '../context/WorkspaceContext'
import { useQuickStart } from '../context/QuickStartContext'
import { useSubscription } from '../hooks/useSubscription'
import { useToast } from '../context/ToastContext'
import Navbar from '../components/Navbar'
import Tooltip from '../components/Tooltip'
import OrgSwitcher from '../components/OrgSwitcher'
import SyncPreviewModal from '../components/SyncPreviewModal'
import SyncProgressIndicator from '../components/SyncProgressIndicator'
import { ReauthModal } from '../components/ReauthModal'
import TrialExportLimitModal from '../components/TrialExportLimitModal'
import { updateSalesforceOrgSettings, checkFlowsForUpdates, refreshAllFlows, getAuthToken } from '../services/salesforceService'
import { supabase } from '../services/supabaseClient'
import Chatbot from '../components/Chatbot'
import { exportMultipleFlowsToPDF } from '../utils/multiFlowPdfExportSimple'
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient'

export default function FlowsPage() {
  const { user, signOut } = useAuth()
  const { orgs, isLoading: isLoadingOrgs, syncOrg, updateOrgSettings, userRole } = useSalesforce()
  const { workspaces, currentWorkspace, setCurrentWorkspace, currentOrgId, setCurrentOrgId } = useWorkspace()
  const { markStepComplete, stepsCompleted } = useQuickStart()
  const { subscription } = useSubscription()
  const { showToast } = useToast()
  const [searchParams] = useSearchParams()
  const { orgId: urlOrgId } = useParams<{ orgId: string }>()
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [flows, setFlows] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [flowType, setFlowType] = useState<string>('all')
  const [selectedGrades, setSelectedGrades] = useState<Set<string>>(new Set(['A', 'B', 'C', 'D', 'I', 'Unscored']))
  const [performanceIssue, setPerformanceIssue] = useState<string>('all')
  const [isRefreshingAll, setIsRefreshingAll] = useState(false)
  const [isScoringAll, setIsScoringAll] = useState(false)
  const [sortField, setSortField] = useState<string>('label')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [showActiveOnly, setShowActiveOnly] = useState(true)
  const [packageFilter, setPackageFilter] = useState<string>('all')
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false)
  const [showSyncPreview, setShowSyncPreview] = useState(false)
  const [syncPreviewData, setSyncPreviewData] = useState<any>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [showExportDropdown, setShowExportDropdown] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const exportDropdownRef = useRef<HTMLDivElement>(null)
  const [showSyncProgress, setShowSyncProgress] = useState(false)
  const [syncTotalFlows, setSyncTotalFlows] = useState(0)
  const [showReauthModal, setShowReauthModal] = useState(false)
  const [reauthOrgId, setReauthOrgId] = useState<string | null>(null)
  const [showTrialExportModal, setShowTrialExportModal] = useState(false)

  // Context-aware filtering states
  const [filterContext, setFilterContext] = useState<string | null>(null)
  const [contextMessage, setContextMessage] = useState<string | null>(null)
  const [dateFilter, setDateFilter] = useState<string | null>(null)
  const [errorTypeFilter, setErrorTypeFilter] = useState<string | null>(null)
  const [criticalFilter, setCriticalFilter] = useState(false)
  const [impactedUsersFilter, setImpactedUsersFilter] = useState(false)
  const [issueTypeFromUrl, setIssueTypeFromUrl] = useState<string | null>(null)
  
  const navigate = useNavigate()
  const location = useLocation()
  
  // Check for sync preview data from navigation state
  useEffect(() => {
    if (location.state?.syncPreviewData && searchParams.get('showSyncPreview') === 'true') {
      setSyncPreviewData(location.state.syncPreviewData)
      setShowSyncPreview(true)
      // Clear the state to prevent showing it again on refresh
      navigate(location.pathname + location.search, { replace: true, state: {} })
    }
  }, [location, searchParams, navigate])
  
  // Parse context-aware URL parameters
  useEffect(() => {
    const context = searchParams.get('context')
    const issueType = searchParams.get('issueType')
    const grade = searchParams.get('grade')
    const filter = searchParams.get('filter')
    const date = searchParams.get('date')
    const errorType = searchParams.get('errorType')
    
    setFilterContext(context)
    
    // Reset filters first
    setDateFilter(null)
    setErrorTypeFilter(null)
    setCriticalFilter(false)
    setImpactedUsersFilter(false)
    
    if (context === 'issueDistribution' && issueType) {
      // Store the original issue type from URL for API calls
      setIssueTypeFromUrl(issueType)
      // For display and local filtering, convert to lowercase-hyphenated
      const convertedIssueType = issueType.toLowerCase().replace(/_/g, '-')
      setPerformanceIssue(convertedIssueType)
      setContextMessage(`Showing flows with "${issueType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}" issues`)
    } else if (context === 'metrics') {
      if (grade === 'I') {
        setSelectedGrades(new Set(['I']))
        setContextMessage('Showing incomplete flows')
      } else if (filter === 'critical') {
        setCriticalFilter(true)
        setContextMessage('Showing flows with critical issues')
      } else if (filter === 'impactedUsers') {
        setImpactedUsersFilter(true)
        setContextMessage('Showing flows impacting users')
      }
    } else if (context === 'burndown' && date) {
      setDateFilter(date)
      const formattedDate = new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      setContextMessage(`Showing flows modified on ${formattedDate}`)
    } else if (context === 'errorTypes' && errorType) {
      setErrorTypeFilter(errorType)
      setFilterContext('errorTypes')
      setContextMessage(`Showing flows with "${errorType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}" errors`)
    } else if (context === 'issueTrend' && issueType && date) {
      // Handle clicking on stacked column chart
      setIssueTypeFromUrl(issueType)
      setDateFilter(date)
      setFilterContext('issueTrend')
      const convertedIssueType = issueType.toLowerCase().replace(/_/g, '-')
      setPerformanceIssue(convertedIssueType)
      setContextMessage(`Showing flows with "${issueType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}" issues on ${new Date(date).toLocaleDateString()}`)
    } else if (context === 'issuesFixed' && date) {
      // Handle clicking on Issues Fixed chart
      setDateFilter(date)
      setFilterContext('issuesFixed')
      setContextMessage(`Showing flows that had issues fixed on ${new Date(date).toLocaleDateString()}`)
    } else {
      setContextMessage(null)
      setIssueTypeFromUrl(null)
    }
  }, [searchParams])

  // Auto-navigate to first flow for Quick Start Step 5
  useEffect(() => {
    if (searchParams.get('autoSelectFirst') === 'true' && flows.length > 0 && selectedOrgId) {
      const firstFlow = flows[0]
      const flowId = firstFlow.id || firstFlow.Id || firstFlow.api_name || firstFlow.ApiName
      if (flowId) {
        // Navigate to first flow detail page
        navigate(`/${encodeURIComponent(selectedOrgId)}/flow/${encodeURIComponent(flowId)}`, { replace: true })
      }
    }
  }, [searchParams, flows, selectedOrgId, navigate])

  // Set the selected org from URL params or default to first org
  useEffect(() => {
    // First check if we have orgId in the URL path
    if (urlOrgId) {
      setSelectedOrgId(urlOrgId)
      setCurrentOrgId(urlOrgId) // Sync with workspace context
    } 
    // Fall back to query params for backward compatibility
    else {
      const orgId = searchParams.get('orgId')
      if (orgId) {
        setSelectedOrgId(orgId)
        setCurrentOrgId(orgId) // Sync with workspace context
        // Redirect to new URL format
        navigate(`/${orgId}/flows`, { replace: true })
      } else if (orgs.length > 0 && !selectedOrgId) {
        // Default to first org and redirect to new URL format
        const firstOrgId = orgs[0].id
        setSelectedOrgId(firstOrgId)
        setCurrentOrgId(firstOrgId) // Sync with workspace context
        navigate(`/${firstOrgId}/flows`, { replace: true })
      }
    }
  }, [urlOrgId, searchParams, orgs, selectedOrgId, setCurrentOrgId, navigate])

  // Mark Quick Start step 3 complete when flows are viewed
  useEffect(() => {
    if (flows.length > 0 && !stepsCompleted.exploredDashboard) {
      markStepComplete('exploredDashboard');
    }
  }, [flows.length, stepsCompleted.exploredDashboard, markStepComplete]);

  // Get org settings when org changes
  useEffect(() => {
    if (selectedOrgId) {
      const org = orgs.find(o => o.id === selectedOrgId)
      if (org && org.settings) {
        // Set to true if undefined or null, only set to false if explicitly false
        setShowActiveOnly(org.settings.filter_active_only !== false)
        setPackageFilter(org.settings.filter_package_state || 'all')
        
        // Removed automatic polling mechanism:
        // We no longer automatically check for updates to prevent constant
        // API calls that could lead to CORS issues or rate limiting.
        // Users will need to manually click the "Check Updates" button instead.
        console.log("Setting loaded from organization settings. Use 'Check Updates' button to manually check for flow updates.");
      }
    }
  }, [selectedOrgId, orgs])

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // Use a ref to track if we've already fetched flows for this org
  const fetchedOrgsRef = useRef(new Set());
  
  // Fetch flows by issue type or scorecard context
  useEffect(() => {
    const fetchFlowsByContext = async () => {
      if (!selectedOrgId) return;
      
      // Determine which API to use based on context
      let apiUrl = null;
      
      if (filterContext === 'issuesFixed' && dateFilter) {
        apiUrl = `/api/flows/by-scorecard-date?orgId=${selectedOrgId}&date=${dateFilter}&context=issuesFixed`;
      } else if (filterContext === 'issueTrend' && issueTypeFromUrl && dateFilter) {
        apiUrl = `/api/flows/by-scorecard-date?orgId=${selectedOrgId}&date=${dateFilter}&issueType=${issueTypeFromUrl}&context=issueTrend`;
      } else if (filterContext === 'errorTypes' && errorTypeFilter) {
        apiUrl = `/api/flows/by-error-type?orgId=${selectedOrgId}&errorType=${errorTypeFilter}`;
      } else if (issueTypeFromUrl && !dateFilter) {
        apiUrl = `/api/flows/by-issue-type?orgId=${selectedOrgId}&issueType=${issueTypeFromUrl}`;
      }
      
      if (!apiUrl) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('No session');
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch flows');
        }
        
        const { flows: contextFlows } = await response.json();
        console.log(`Fetched ${contextFlows.length} flows for context ${filterContext || 'issueType'}`);
        
        // Transform and set the flows
        const transformedFlows = contextFlows.map((flow: any) => ({
          ...flow,
          // Ensure sf_flow_summaries is always an array
          sf_flow_summaries: Array.isArray(flow.sf_flow_summaries) ? flow.sf_flow_summaries : []
        }));
        
        setFlows(transformedFlows);
        // Mark as fetched to prevent regular fetch
        fetchedOrgsRef.current.add(selectedOrgId);
        
      } catch (err: any) {
        console.error('Error fetching flows by issue type:', err);
        setError(err);
        setFlows([]); // Clear flows on error - no fallback!
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlowsByContext();
  }, [selectedOrgId, issueTypeFromUrl, dateFilter, filterContext, errorTypeFilter]);
  
  // Fetch flows when org is initially selected - only once per org
  useEffect(() => {
    const fetchFlows = async () => {
      if (!selectedOrgId) return;
      
      // Skip if already loading
      if (isLoading) return;
      
      // Skip if we've already fetched this org in this session
      if (fetchedOrgsRef.current.has(selectedOrgId)) {
        console.log(`Already fetched flows for org ${selectedOrgId} in this session`);
        return;
      }
      
      // Skip if we're fetching by issue type or error type
      if (issueTypeFromUrl || errorTypeFilter) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch from the sf_flow_definitions table with summary info and performance scores
        const { data, error: fetchError } = await supabase
          .from('sf_flow_definitions')
          .select(`
            *,
            performance_grade,
            performance_score,
            performance_issues,
            performance_calculated_at,
            performance_summary,
            sf_flow_summaries!left(
              id,
              workspace_id,
              ai_summary,
              step_by_step_analysis,
              workspaces!inner(
                id,
                name,
                is_active
              )
            )
          `)
          .eq('org_id', selectedOrgId)
          .order('master_label');
          
        if (fetchError) {
          console.error('Error fetching flows:', fetchError);
          throw fetchError;
        }
        
        // Transform the data to match the old format for compatibility
        const transformedFlows = (data || []).map(flow => {
          // Ensure sf_flow_summaries is an array
          // Supabase returns null for empty relationships
          const summaries = flow.sf_flow_summaries;
          const summariesArray = Array.isArray(summaries) ? summaries : (summaries === null ? [] : [summaries]);
          
          // Queue items removed - no longer needed with streamlined architecture
          
          return {
            ...flow,
            // Map new field names to old names for compatibility with existing filter logic
            MasterLabel: flow.master_label,
            ApiName: flow.developer_name,
            FullName: flow.developer_name,
            DeveloperName: flow.developer_name,
            Status: flow.status,
            active: flow.status === 'Active',
            ProcessType: flow.process_type,
            Description: flow.description,
            LastModifiedDate: flow.last_modified_date,
            // Keep original fields too
            name: flow.master_label,
            label: flow.master_label,
            api_name: flow.api_name,
            full_name: flow.api_name,
            status: flow.status,
            process_type: flow.process_type,
            description: flow.description,
            last_modified_date: flow.last_modified_date,
            // Map summary_status to app_status for display
            app_status: flow.summary_status || 'pending',
            // Ensure sf_flow_summaries is always an array
            sf_flow_summaries: summariesArray
          };
        });
        
        console.log(`Fetched ${transformedFlows.length} flows from sf_flow_definitions`);
        // Debug: Check what sf_flow_summaries looks like
        if (transformedFlows.length > 0) {
          console.log('Sample flow structure:', transformedFlows[0]);
          console.log('sf_flow_summaries type:', typeof transformedFlows[0].sf_flow_summaries);
          console.log('sf_flow_summaries value:', transformedFlows[0].sf_flow_summaries);
        }
        setFlows(transformedFlows);
        fetchedOrgsRef.current.add(selectedOrgId);
        
      } catch (err: any) {
        console.error('Error fetching flows:', err);
        setError(err);
        setFlows([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlows();
  }, [selectedOrgId, issueTypeFromUrl, errorTypeFilter]);
  
  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }
  
  const handleFlowClick = (flowId: string) => {
    navigate(`/${encodeURIComponent(selectedOrgId || '')}/flow/${encodeURIComponent(flowId)}`);
  }

  const handleExportMarkdown = async () => {
    setShowExportDropdown(false)
    setIsExporting(true)
    
    try {
      // Get all flow details
      const flowDetailsPromises = filteredFlows.map(async (flow) => {
        const { data, error } = await supabase
          .from('sf_flow_definitions')
          .select(`
            *,
            sf_flow_summaries(*),
            sf_flow_elements(*)
          `)
          .eq('id', flow.id)
          .single()
        
        if (error) throw error
        return data
      })
      
      const flowDetails = await Promise.all(flowDetailsPromises)
      
      // Build markdown content with page breaks
      let markdownContent = `# Flow Documentation Export\n\n`
      markdownContent += `**Organization:** ${orgs.find(o => o.id === selectedOrgId)?.name || 'Unknown'}\n`
      markdownContent += `**Export Date:** ${new Date().toLocaleString()}\n`
      markdownContent += `**Total Flows:** ${flowDetails.length}\n\n`
      markdownContent += `---\n\n`
      
      flowDetails.forEach((flow, index) => {
        markdownContent += `## ${flow.label || flow.api_name}\n\n`
        markdownContent += `**API Name:** ${flow.api_name}\n`
        markdownContent += `**Type:** ${flow.process_type}\n`
        markdownContent += `**Status:** ${flow.status}\n`
        markdownContent += `**Performance Grade:** ${flow.performance_grade || 'Unscored'}\n`
        if (flow.description) {
          markdownContent += `**Description:** ${flow.description}\n`
        }
        markdownContent += `\n`
        
        // Add summary if available
        const summary = flow.sf_flow_summaries?.[0]
        if (summary?.content) {
          markdownContent += `### Summary\n\n${summary.content}\n\n`
        }
        
        // Add trigger information if available
        if (flow.trigger_info) {
          markdownContent += `### Trigger Information\n\n`
          if (flow.trigger_info.triggerType) {
            markdownContent += `**Trigger Type:** ${flow.trigger_info.triggerType}\n`
          }
          if (flow.trigger_info.objectType) {
            markdownContent += `**Object:** ${flow.trigger_info.objectType}\n`
          }
          if (flow.trigger_info.recordTriggerType) {
            markdownContent += `**When:** ${flow.trigger_info.recordTriggerType}\n`
          }
          markdownContent += `\n`
        }
        
        // Add flow elements summary
        if (flow.sf_flow_elements && flow.sf_flow_elements.length > 0) {
          const elementTypes = flow.sf_flow_elements.reduce((acc: any, element: any) => {
            acc[element.element_type] = (acc[element.element_type] || 0) + 1
            return acc
          }, {})
          
          markdownContent += `### Flow Elements (${flow.sf_flow_elements.length} total)\n\n`
          Object.entries(elementTypes).forEach(([type, count]) => {
            markdownContent += `- ${type}: ${count}\n`
          })
          markdownContent += `\n`
        }
        
        // Add metadata
        if (flow.metadata) {
          markdownContent += `### Additional Details\n\n`
          if (flow.metadata.activeVersionNumber) {
            markdownContent += `**Version:** ${flow.metadata.activeVersionNumber}\n`
          }
          if (flow.metadata.lastModifiedDate) {
            markdownContent += `**Last Modified:** ${new Date(flow.metadata.lastModifiedDate).toLocaleDateString()}\n`
          }
          markdownContent += `\n`
        }
        
        // Add page break between flows (except for the last one)
        if (index < flowDetails.length - 1) {
          markdownContent += `\n<div style="page-break-after: always;"></div>\n\n`
          markdownContent += `---\n\n`
        }
      })
      
      // Download the file
      const blob = new Blob([markdownContent], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `flow-documentation-${selectedOrgId}-${new Date().toISOString().split('T')[0]}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
    } catch (error) {
      console.error('Export error:', error)
      showToast('Failed to export flows. Please try again.', 'error')
    } finally {
      setIsExporting(false)
    }
  }

  
  // Helper function to check if flow has a specific issue
  const checkForIssue = (issues: any, issueType: string): boolean => {
    if (!issues) return false;
    
    // Map of issue types to detector names
    const issueMap: { [key: string]: string } = {
      'hardcoded-ids': 'HARDCODED_SALESFORCE_IDS',
      'overly-broad-entry': 'OVERLY_BROAD_ENTRY_CRITERIA',
      'missing-error-handling': 'MISSING_ERROR_HANDLING',
      'dml-in-loops': 'DML_OPERATIONS_IN_LOOPS',
      'queries-in-loops': 'QUERIES_IN_LOOPS',
      'not-bulkified': 'NOT_BULKIFIED',
      'missing-description': 'MISSING_DESCRIPTION',
      'excessive-queries': 'EXCESSIVE_QUERIES',
      'poor-naming': 'POOR_NAMING_CONVENTION',
      'high-complexity': 'HIGH_COMPLEXITY',
      'deep-nesting': 'DEEP_NESTING',
      'ischanged-missing': 'ISCHANGED_MISSING',
      'recursive-update': 'RECURSIVE_SAME-RECORD_UPDATE'
    };
    
    const targetIssue = issueMap[issueType];
    if (!targetIssue) return false;
    
    // Check if deductions array contains the issue
    if (issues.deductions && Array.isArray(issues.deductions)) {
      return issues.deductions.some((d: any) => 
        d.name && d.name.toUpperCase().replace(/ /g, '_') === targetIssue
      );
    }
    
    // Check if showstoppers array contains the issue
    if (issues.showstoppers && Array.isArray(issues.showstoppers)) {
      return issues.showstoppers.some((s: any) => 
        s.name && s.name.toUpperCase().replace(/ /g, '_') === targetIssue
      );
    }
    
    return false;
  }
  
  // Calculate issue counts
  const calculateIssueCounts = () => {
    const counts: { [key: string]: number } = {
      'hardcoded-ids': 0,
      'overly-broad-entry': 0,
      'missing-error-handling': 0,
      'dml-in-loops': 0,
      'queries-in-loops': 0,
      'not-bulkified': 0,
      'missing-description': 0,
      'excessive-queries': 0,
      'poor-naming': 0,
      'high-complexity': 0,
      'deep-nesting': 0,
      'ischanged-missing': 0,
      'recursive-update': 0
    };
    
    flows.forEach(flow => {
      if (flow.performance_issues) {
        Object.keys(counts).forEach(issueType => {
          if (checkForIssue(flow.performance_issues, issueType)) {
            counts[issueType]++;
          }
        });
      }
    });
    
    return counts;
  }
  
  const issueCounts = calculateIssueCounts();
  
  const handleCheckUpdates = async () => {
    if (!selectedOrgId) return;
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Checking for flow updates for org ${selectedOrgId}`);
      
      // Get fresh tokens from client
      const tokens = await getAuthToken(selectedOrgId);
      
      // Call enhanced check updates endpoint
      const endpoint = '/api/salesforce/flows/check-updates-enhanced';
      let result;
      
      if (isEdgeFunctionMigrated(endpoint)) {
        // Use Edge Function
        const functionName = getEdgeFunctionName(endpoint);
        result = await callEdgeFunction(functionName!, {
          orgId: selectedOrgId,
          access_token: tokens.access_token,
          instance_url: tokens.instance_url
        });
      } else {
        // Fallback to Vercel API
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            orgId: selectedOrgId,
            access_token: tokens.access_token,
            instance_url: tokens.instance_url
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to check for updates');
        }
        
        result = await response.json();
      }
      console.log('Enhanced updates check complete:', result);
      
      if (result.has_updates) {
        // Store the sync preview data and show modal
        setSyncPreviewData(result);
        setShowSyncPreview(true);
      } else {
        // No flow updates, but still queue dashboard metrics update
        console.log('No flow updates found, queueing dashboard metrics update...');
        
        try {
          // Get current session for auth
          const { data: { session } } = await supabase.auth.getSession();
          
          // Call comprehensive sync with updateMetricsOnly flag
          const endpoint = '/api/salesforce/flows/comprehensive-sync';
          const syncResponse = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': session ? `Bearer ${session.access_token}` : '',
              'x-supabase-auth': session ? session.access_token : ''
            },
            body: JSON.stringify({
              orgId: selectedOrgId,
              updateMetricsOnly: true
            })
          });
          
          if (syncResponse.ok) {
            showToast('All flows are up to date! Dashboard metrics have been refreshed.', 'info');
          } else {
            showToast('All flows are up to date!', 'info');
          }
        } catch (error) {
          console.error('Error updating metrics:', error);
          showToast('All flows are up to date!', 'info');
        }
      }
    } catch (err: any) {
      console.error('Error checking for updates:', err);
      
      // Handle authentication errors
      if (err.message === 'INVALID_SESSION' || err.requiresReauth || err.needsReconnect) {
        setReauthOrgId(selectedOrgId);
        setShowReauthModal(true);
      } else {
        setError(err);
        showToast(`Error: ${err.message || 'Failed to check for updates'}`, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleConfirmSync = async () => {
    if (!selectedOrgId || !syncPreviewData) return;
    
    setIsSyncing(true);
    setError(null);
    
    try {
      console.log('Starting comprehensive sync...');
      
      const { data: org } = await supabase
        .from('sf_orgs')
        .select('access_token, instance_url')
        .eq('id', selectedOrgId)
        .single();
      
      if (!org) {
        throw new Error('Organization not found');
      }
      
      // Call comprehensive sync endpoint with sync plan
      const endpoint = '/api/salesforce/flows/comprehensive-sync';
      
      // Get current session for auth
      const { data: { session } } = await supabase.auth.getSession();
      
      const syncResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': session ? `Bearer ${session.access_token}` : '',
          'x-supabase-auth': session ? session.access_token : ''
        },
        body: JSON.stringify({
          orgId: selectedOrgId,
          access_token: org.access_token,
          instance_url: org.instance_url,
          syncPlan: syncPreviewData.details
        })
      });
      
      if (!syncResponse.ok) {
        throw new Error('Sync failed');
      }
      
      const syncResult = await syncResponse.json();
      console.log('Comprehensive sync complete:', syncResult);

      // Extract counts from sync preview data
      const newFlows = syncPreviewData.details.new_flows?.length || 0;
      const updatedFlows = syncPreviewData.details.updated_flows?.length || 0;
      const deletedFlows = syncPreviewData.details.deleted_flows?.length || 0;
      const totalChanges = newFlows + updatedFlows + deletedFlows;

      // Close modal and show progress indicator
      setShowSyncPreview(false);
      setSyncTotalFlows(totalChanges);
      setShowSyncProgress(true);

      // Clear the fetched orgs cache to allow refetch
      fetchedOrgsRef.current.delete(selectedOrgId);

      // Wait for sync to complete and progress indicator to show, then refetch
      setTimeout(async () => {
        setShowSyncProgress(false);

        // Refetch flows to show updated data
        try {
          setIsLoading(true);
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('No session');

          const response = await fetch(`/api/flows?orgId=${selectedOrgId}`, {
            headers: { 'Authorization': `Bearer ${session.access_token}` }
          });

          if (response.ok) {
            const { flows: refreshedFlows } = await response.json();
            const transformedFlows = refreshedFlows.map((flow: any) => ({
              ...flow,
              sf_flow_summaries: Array.isArray(flow.sf_flow_summaries) ? flow.sf_flow_summaries : []
            }));
            setFlows(transformedFlows);
            console.log(`Refreshed ${refreshedFlows.length} flows after sync`);

            // Show success toast with counts
            showToast(
              `Sync complete! ${newFlows} new, ${updatedFlows} updated, ${deletedFlows} deleted.`,
              'success'
            );
          }
        } catch (err) {
          console.error('Error refetching flows after sync:', err);
          showToast('Sync complete, but failed to refresh flow list. Please reload the page.', 'error');
        } finally {
          setIsLoading(false);
        }
      }, 2000);

    } catch (syncError) {
      console.error('Error during comprehensive sync:', syncError);
      setError(syncError as Error);
      showToast(`Sync failed: ${syncError instanceof Error ? syncError.message : 'Unknown error'}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };
  
  const handleRefreshAll = async () => {
    if (!selectedOrgId) return;
    
    const confirmRefresh = window.confirm(
      'This will refresh all flows from Salesforce.\n\n' +
      'This may take a few minutes for organizations with many flows.\n\n' +
      'Continue?'
    );
    
    if (!confirmRefresh) return;
    
    setIsRefreshingAll(true);
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Starting full refresh for org ${selectedOrgId}`);
      const result = await refreshAllFlows(selectedOrgId);
      console.log('Full refresh complete:', result);
      
      // Clear the fetched orgs cache so the data will be reloaded
      fetchedOrgsRef.current.delete(selectedOrgId);
      
      // Reload the page to show fresh data
      window.location.reload();
    } catch (err: any) {
      console.error('Error during full refresh:', err);
      setError(err);
    } finally {
      setIsRefreshingAll(false);
      setIsLoading(false);
    }
  };
  
  const handleSettingsUpdate = async () => {
    if (!selectedOrgId) return;
    
    setIsUpdatingSettings(true);
    try {
      const result = await updateOrgSettings(
        selectedOrgId,
        showActiveOnly,
        packageFilter
      );
      console.log('Settings updated:', result);
    } catch (err: any) {
      console.error('Error updating settings:', err);
      setError(err);
    } finally {
      setIsUpdatingSettings(false);
    }
  };
  
  // Sort handler
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Helper function to get display value with fallback
  const getDisplayValue = (flow: any, fieldName: string, fallback = 'N/A'): string => {
    // Try new field names first, then old field names
    const fieldMap: { [key: string]: string[] } = {
      'name': ['master_label', 'MasterLabel', 'label', 'name'],
      'apiName': ['api_name', 'ApiName', 'DeveloperName'],
      'processType': ['process_type', 'ProcessType'],
      'status': ['status', 'Status'],
      'description': ['description', 'Description'],
      'lastModified': ['last_modified_date', 'LastModifiedDate']
    };
    
    const possibleFields = fieldMap[fieldName] || [fieldName];
    
    for (const field of possibleFields) {
      if (flow[field] !== undefined && flow[field] !== null) {
        return flow[field];
      }
    }
    
    return fallback;
  };
  
  // Helper function to get flow ID
  const getFlowId = (flow: any): string => {
    return flow.id || flow.sf_flow_id || flow.Id || '';
  };
  
  // Filter flows based on search term, workspace, and other criteria
  const filteredFlows = Array.isArray(flows) ? flows.filter(flow => {
    // Skip filtering if we're in a context mode where flows were already filtered by the API
    if (filterContext && (filterContext === 'issueTrend' || filterContext === 'issuesFixed' || filterContext === 'errorTypes')) {
      // Only apply search filter in context modes
      if (searchTerm && !flow.MasterLabel?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.FullName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.ApiName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.Description?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.label?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.api_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.description?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !flow.master_label?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false
      }
      return true; // Skip all other filters when in context mode
    }
    
    // Don't filter by workspace - show all flows for the org
    // The summary status will indicate if this workspace has processed it
    // Apply search filter using both old and new field names
    if (searchTerm && !flow.MasterLabel?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.FullName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.ApiName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.Description?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.label?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.api_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.description?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !flow.master_label?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false
    }
    
    // Apply process type filter (using both old and new field names)
    if (flowType !== 'all') {
      // Special handling for Record-Triggered Flows
      if (flowType === 'RecordTriggered') {
        // Check trigger_info for record-triggered flow types
        const triggerType = flow.trigger_info?.triggerType || '';
        const recordTriggerTypes = ['RecordAfterSave', 'RecordBeforeSave', 'RecordBeforeDelete', 'Before Save', 'After Save'];
        if (!recordTriggerTypes.includes(triggerType)) {
          return false;
        }
      } else {
        // For other flow types, check ProcessType
        if (flow.ProcessType !== flowType && flow.process_type !== flowType) {
          return false;
        }
      }
    }
    
    // Apply performance grade filter (multi-select)
    if (selectedGrades.size > 0 && selectedGrades.size < 6) { // If not all grades are selected
      let flowGrade = flow.performance_grade || 'Unscored';
      // Map 'F' grades to 'I' for filtering
      if (flowGrade === 'F') flowGrade = 'I';
      if (!selectedGrades.has(flowGrade)) {
        return false;
      }
    }
    
    // Apply performance issue filter (skip if we already filtered by issue type via API)
    if (!issueTypeFromUrl && performanceIssue !== 'all' && flow.performance_issues) {
      const issues = flow.performance_issues;
      let hasIssue = false;
      
      // Parse the issues if they're stored as JSON
      if (typeof issues === 'string') {
        try {
          const parsedIssues = JSON.parse(issues);
          hasIssue = checkForIssue(parsedIssues, performanceIssue);
        } catch (e) {
          // If parsing fails, skip this flow
        }
      } else if (typeof issues === 'object') {
        hasIssue = checkForIssue(issues, performanceIssue);
      }
      
      if (!hasIssue) {
        return false;
      }
    }
    
    // Apply context-aware filters
    if (criticalFilter) {
      // Filter for flows with critical issues (high severity)
      if (!flow.performance_issues) return false
      
      const issues = typeof flow.performance_issues === 'string' 
        ? JSON.parse(flow.performance_issues) 
        : flow.performance_issues
      
      const hasCriticalIssue = (issues.deductions || []).some((d: any) => 
        d.severity === 'high' || d.points >= 10
      ) || (issues.showstoppers || []).length > 0
      
      if (!hasCriticalIssue) return false
    }
    
    if (impactedUsersFilter) {
      // This would require additional data about flow errors
      // For now, we'll show flows with poor grades or known issues
      const grade = flow.performance_grade || 'Unscored'
      if (!['D', 'F', 'I'].includes(grade)) return false
    }
    
    if (dateFilter) {
      // Filter flows modified on a specific date
      const flowDate = flow.last_modified_date || flow.LastModifiedDate
      if (!flowDate) return false
      
      // Parse the flow date and the filter date
      const flowDateObj = new Date(flowDate)
      const filterDateObj = new Date(dateFilter + 'T00:00:00')
      
      // Compare just the date parts (ignoring time and timezone)
      const flowYear = flowDateObj.getFullYear()
      const flowMonth = flowDateObj.getMonth()
      const flowDay = flowDateObj.getDate()
      
      const filterYear = filterDateObj.getFullYear()
      const filterMonth = filterDateObj.getMonth()
      const filterDay = filterDateObj.getDate()
      
      if (flowYear !== filterYear || flowMonth !== filterMonth || flowDay !== filterDay) {
        return false
      }
    }
    
    if (errorTypeFilter) {
      // When errorTypeFilter is set, flows are already filtered by the API
      // The by-error-type endpoint only returns flows with the specified error type
      // So we should show all flows returned by the API when this filter is active
      // The error_types array is added by the API for reference
      return true
    }
    
    // We only show active flows - all synced flows are active by default
    
    return true
  }).sort((a, b) => {
    // Get values for sorting
    let aValue: any;
    let bValue: any;
    
    switch (sortField) {
      case 'label':
        aValue = getDisplayValue(a, 'name', '').toLowerCase();
        bValue = getDisplayValue(b, 'name', '').toLowerCase();
        break;
      case 'type':
        aValue = getDisplayValue(a, 'processType', '').toLowerCase();
        bValue = getDisplayValue(b, 'processType', '').toLowerCase();
        break;
      case 'performance':
        // Sort by actual score (higher scores first when ascending)
        aValue = a.performance_score || -1; // Unscored flows get -1
        bValue = b.performance_score || -1;
        break;
      case 'lastModified':
        aValue = new Date(getDisplayValue(a, 'lastModified', '0')).getTime();
        bValue = new Date(getDisplayValue(b, 'lastModified', '0')).getTime();
        break;
      case 'summary':
        // Sort by summary status priority: completed > processing > pending > failed > new
        const statusPriority: { [key: string]: number } = {
          'completed': 1,
          'processing': 2,
          'pending': 3,
          'failed': 4,
          'new': 5
        };
        
        // Determine status for each flow
        const getStatus = (flow: any) => {
          if (currentWorkspace) {
            const workspaceSummary = flow.sf_flow_summaries?.find((s: any) => 
              s.workspace_id === currentWorkspace.id
            );
            
            if (workspaceSummary) {
              return 'completed';
            } else {
              // No summary for this workspace - it's new
              return 'new';
            }
          } else {
            return flow.app_status || 'pending';
          }
        };
        
        aValue = statusPriority[getStatus(a)] || 6;
        bValue = statusPriority[getStatus(b)] || 6;
        break;
      default:
        aValue = '';
        bValue = '';
    }
    
    // Compare values
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  }) : []
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={handleSignOut} />

      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold leading-tight text-gray-900">
                Flows
              </h1>

              {/* Org Switcher */}
              <OrgSwitcher currentOrgId={selectedOrgId} />

              {/* Org Selector - Hidden per user request - functionality remains in navbar
              <div className="flex items-center gap-2">
                <label htmlFor="org-select" className="text-sm font-medium text-gray-700">
                  Organization:
                </label>
                <select
                  id="org-select"
                  value={selectedOrgId || ''}
                  onChange={(e) => {
                    const newOrgId = e.target.value;
                    if (newOrgId && newOrgId !== selectedOrgId) {
                      // Clear the fetched cache when switching orgs
                      fetchedOrgsRef.current.clear();
                      setSelectedOrgId(newOrgId);
                      setCurrentOrgId(newOrgId);
                      navigate(`/${newOrgId}/flows`);
                    }
                  }}
                  className="block w-64 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  disabled={isLoading}
                >
                  <option value="">Select an organization</option>
                  {orgs.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name} {org.is_sandbox ? '(Sandbox)' : '(Production)'}
                    </option>
                  ))}
                </select>
              </div>
              */}
              
              <div className={`flex gap-4 ${userRole && userRole !== 'Viewer' ? 'action-buttons' : ''}`}>
                {/* Check for New and Updated Flows button - only show for non-Viewer roles when role is loaded */}
                {userRole && userRole !== 'Viewer' && (
                  <Tooltip content="Check for new, modified, or deleted flows and sync changes">
                    <button
                      type="button"
                      onClick={handleCheckUpdates}
                      disabled={isLoading || !selectedOrgId}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Check for New and Updated Flows
                    </button>
                  </Tooltip>
                )}

                {/* Export dropdown - only show for non-Viewer roles when role is loaded */}
                {userRole && userRole !== 'Viewer' && (
                  <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      // Trial users: show modal explaining limitation (check this FIRST)
                      if (subscription.is_trial) {
                        setShowTrialExportModal(true);
                        return;
                      }

                      // Pro/Enterprise users: show export dropdown
                      if (subscription.can_export) {
                        setShowExportDropdown(!showExportDropdown);
                        return;
                      }

                      // Starter users: prompt to upgrade
                      showToast('Export functionality is available with Professional or Enterprise plans. Please upgrade to export flows.', 'info');
                      navigate('/plans');
                    }}
                    disabled={isLoading || filteredFlows.length === 0}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {isExporting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                        Export All Flows
                        <svg className="ml-2 -mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </>
                    )}
                  </button>
                  
                  {showExportDropdown && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        <button
                          onClick={handleExportMarkdown}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Export as Markdown
                        </button>
                        <button
                          onClick={async () => {
                            setShowExportDropdown(false);
                            setIsExporting(true);
                            try {
                              const selectedOrg = orgs.find(o => o.id === selectedOrgId);
                              const orgName = selectedOrg?.name || 'flows';
                              
                              // Get org data for building Salesforce URLs
                              const { data: orgData } = await supabase
                                .from('sf_orgs')
                                .select('instance_url')
                                .eq('id', selectedOrgId)
                                .single();
                              
                              // Export all visible flows to a single PDF
                              await exportMultipleFlowsToPDF(
                                filteredFlows,
                                selectedOrgId || '',
                                orgName,
                                orgData,
                                workspaces,
                                {
                                  includeDate: true,
                                  onProgress: (current, total, flowName) => {
                                    console.log(`Exporting flow ${current}/${total}: ${flowName}`);
                                  }
                                }
                              );
                            } catch (error) {
                              console.error('PDF export error:', error);
                              alert('Failed to export PDF. Please try again.');
                            } finally {
                              setIsExporting(false);
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Export as PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            </div>
            
          </div>
        </header>
        
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              <div id="flows-list-content" className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  {error && (
                    <div className="mb-4 rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">
                            {error.message || 'An error occurred while loading flows'}
                          </h3>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Context message for filtered views */}
                  {contextMessage && (
                    <div className="mb-4 rounded-md bg-blue-50 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-blue-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <h3 className="text-sm font-medium text-blue-800">
                            {contextMessage}
                          </h3>
                        </div>
                        <button
                          onClick={() => {
                            // Clear context filters
                            const newParams = new URLSearchParams(searchParams)
                            newParams.delete('context')
                            newParams.delete('issueType')
                            newParams.delete('grade')
                            newParams.delete('filter')
                            newParams.delete('date')
                            newParams.delete('errorType')
                            navigate(`/${selectedOrgId}/flows?${newParams.toString()}`)
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Clear filter
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Performance List View */}
                  <>
                  {/* Performance Summary - Only show in list view */}
                  {flows.length > 0 && (
                    <div className="mb-6 bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">Organization Performance Summary</h3>
                        <button
                          onClick={() => {
                            if (selectedGrades.size === 6) {
                              setSelectedGrades(new Set());
                            } else {
                              setSelectedGrades(new Set(['A', 'B', 'C', 'D', 'I', 'Unscored']));
                            }
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {selectedGrades.size === 6 ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        {(() => {
                          const gradeCounts = flows.reduce((acc, flow) => {
                            let grade = flow.performance_grade || 'Unscored';
                            // Map 'F' grades to 'I' for incomplete
                            if (grade === 'F') grade = 'I';
                            acc[grade] = (acc[grade] || 0) + 1;
                            return acc;
                          }, {} as Record<string, number>);
                          
                          const scoredFlows = flows.filter(f => f.performance_score !== null && f.performance_score !== undefined);
                          const avgScore = scoredFlows.length > 0 
                            ? scoredFlows.reduce((sum, f) => sum + f.performance_score, 0) / scoredFlows.length 
                            : 0;
                          
                          return (
                            <>
                              {['A', 'B', 'C', 'D', 'I', 'Unscored'].map(grade => {
                                const isSelected = selectedGrades.has(grade);
                                const gradeStyles = {
                                  'A': {
                                    text: isSelected ? 'text-green-700' : 'text-gray-500',
                                    bg: isSelected ? 'bg-green-100' : 'bg-gray-100',
                                    border: isSelected ? 'border-green-200' : 'border-gray-300',
                                    hoverBg: 'hover:bg-green-50'
                                  },
                                  'B': {
                                    text: isSelected ? 'text-blue-700' : 'text-gray-500',
                                    bg: isSelected ? 'bg-blue-100' : 'bg-gray-100',
                                    border: isSelected ? 'border-blue-200' : 'border-gray-300',
                                    hoverBg: 'hover:bg-blue-50'
                                  },
                                  'C': {
                                    text: isSelected ? 'text-yellow-700' : 'text-gray-500',
                                    bg: isSelected ? 'bg-yellow-100' : 'bg-gray-100',
                                    border: isSelected ? 'border-yellow-200' : 'border-gray-300',
                                    hoverBg: 'hover:bg-yellow-50'
                                  },
                                  'D': {
                                    text: isSelected ? 'text-orange-700' : 'text-gray-500',
                                    bg: isSelected ? 'bg-orange-100' : 'bg-gray-100',
                                    border: isSelected ? 'border-orange-200' : 'border-gray-300',
                                    hoverBg: 'hover:bg-orange-50'
                                  },
                                  'I': {
                                    text: isSelected ? 'text-gray-700' : 'text-gray-500',
                                    bg: isSelected ? 'bg-gray-100' : 'bg-gray-50',
                                    border: isSelected ? 'border-gray-300' : 'border-gray-200',
                                    hoverBg: 'hover:bg-gray-100'
                                  },
                                  'Unscored': {
                                    text: isSelected ? 'text-gray-600' : 'text-gray-400',
                                    bg: isSelected ? 'bg-white' : 'bg-gray-50',
                                    border: isSelected ? 'border-gray-300' : 'border-gray-200',
                                    hoverBg: 'hover:bg-gray-50'
                                  }
                                };
                                
                                const style = gradeStyles[grade];
                                const count = gradeCounts[grade] || 0;
                                
                                const toggleGrade = () => {
                                  const newSelectedGrades = new Set(selectedGrades);

                                  // If all grades are currently selected, clicking one should select ONLY that grade
                                  if (selectedGrades.size === 6) {
                                    setSelectedGrades(new Set([grade]));
                                  } else if (newSelectedGrades.has(grade)) {
                                    // If clicking an already-selected grade, deselect it
                                    newSelectedGrades.delete(grade);
                                    setSelectedGrades(newSelectedGrades);
                                  } else {
                                    // If clicking an unselected grade, add it to selection
                                    newSelectedGrades.add(grade);
                                    setSelectedGrades(newSelectedGrades);
                                  }
                                };
                                
                                return (
                                  <Tooltip
                                    key={grade}
                                    content={isSelected ? `Click to hide ${grade === 'I' ? 'Incomplete' : grade} flows` : `Click to show ${grade === 'I' ? 'Incomplete' : grade} flows`}
                                    position="top"
                                    block={true}
                                  >
                                    <button
                                      onClick={toggleGrade}
                                      className={`w-full text-center p-3 rounded-lg border cursor-pointer transition-all ${style.bg} ${style.border} ${style.hoverBg} ${!isSelected ? 'opacity-60' : ''}`}
                                    >
                                      <div className={`text-2xl font-bold ${style.text}`}>
                                        {count}
                                      </div>
                                      <div className={`text-sm font-medium ${style.text}`}>
                                        {grade === 'I' ? 'Incomplete' : grade}
                                      </div>
                                    </button>
                                  </Tooltip>
                                );
                              })}
                              <div className="col-span-6 mt-2 text-center text-sm text-gray-600">
                                Average Score: {avgScore.toFixed(1)} | Total Flows: {flows.length}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  
                  {/* Filters Row - All on one line */}
                  <div className="mb-6 flex gap-4 items-end">
                    {/* Flow Type Filter */}
                    <div className="flex-1">
                      <label htmlFor="flow-type" className="block text-sm font-medium text-gray-700 mb-1">
                        Flow Type
                      </label>
                      <select
                        id="flow-type"
                        className="block w-full px-3 py-3 text-gray-900 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={flowType}
                        onChange={(e) => setFlowType(e.target.value)}
                      >
                        <option value="all">All Types</option>
                        <option value="AutoLaunched">Auto-launched Flow</option>
                        <option value="Flow">Screen Flow</option>
                        <option value="Schedule">Scheduled Flow</option>
                        <option value="RecordTriggered">Record-Triggered Flow</option>
                        <option value="CustomEvent">Platform Event Flow</option>
                        <option value="InvocableProcess">Invocable Process</option>
                      </select>
                    </div>
                    
                    {/* Performance Issue Filter */}
                    <div className="flex-1">
                      <label htmlFor="performance-issue" className="block text-sm font-medium text-gray-700 mb-1">
                        Performance Issue
                      </label>
                      <select
                        id="performance-issue"
                        className="block w-full px-3 py-3 text-gray-900 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={performanceIssue}
                        onChange={(e) => setPerformanceIssue(e.target.value)}
                      >
                        <option value="all">All Flows</option>
                        {issueCounts['hardcoded-ids'] > 0 && (
                          <option value="hardcoded-ids">Hardcoded IDs ({issueCounts['hardcoded-ids']})</option>
                        )}
                        {issueCounts['overly-broad-entry'] > 0 && (
                          <option value="overly-broad-entry">Overly Broad Entry Criteria ({issueCounts['overly-broad-entry']})</option>
                        )}
                        {issueCounts['missing-error-handling'] > 0 && (
                          <option value="missing-error-handling">Missing Error Handling ({issueCounts['missing-error-handling']})</option>
                        )}
                        {issueCounts['dml-in-loops'] > 0 && (
                          <option value="dml-in-loops">DML in Loops ({issueCounts['dml-in-loops']})</option>
                        )}
                        {issueCounts['queries-in-loops'] > 0 && (
                          <option value="queries-in-loops">Queries in Loops ({issueCounts['queries-in-loops']})</option>
                        )}
                        {issueCounts['not-bulkified'] > 0 && (
                          <option value="not-bulkified">Not Bulkified ({issueCounts['not-bulkified']})</option>
                        )}
                        {issueCounts['missing-description'] > 0 && (
                          <option value="missing-description">Missing Description ({issueCounts['missing-description']})</option>
                        )}
                        {issueCounts['excessive-queries'] > 0 && (
                          <option value="excessive-queries">Excessive Queries ({issueCounts['excessive-queries']})</option>
                        )}
                        {issueCounts['poor-naming'] > 0 && (
                          <option value="poor-naming">Poor Naming ({issueCounts['poor-naming']})</option>
                        )}
                        {issueCounts['high-complexity'] > 0 && (
                          <option value="high-complexity">High Complexity ({issueCounts['high-complexity']})</option>
                        )}
                        {issueCounts['deep-nesting'] > 0 && (
                          <option value="deep-nesting">Deep Nesting ({issueCounts['deep-nesting']})</option>
                        )}
                        {issueCounts['ischanged-missing'] > 0 && (
                          <option value="ischanged-missing">Missing ISCHANGED ({issueCounts['ischanged-missing']})</option>
                        )}
                        {issueCounts['recursive-update'] > 0 && (
                          <option value="recursive-update">Recursive Update ({issueCounts['recursive-update']})</option>
                        )}
                      </select>
                    </div>
                    
                    {/* Search Box */}
                    <div className="flex-1">
                      <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                        Search
                      </label>
                      <input
                        type="text"
                        name="search"
                        id="search"
                        className="block w-full px-3 py-3 text-gray-900 border border-gray-300 bg-white rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Search flows by name or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Flows List */}
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table className="min-w-full divide-y divide-gray-300 flows-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              className="group inline-flex items-center hover:text-gray-700"
                              onClick={() => handleSort('label')}
                            >
                              FLOW NAME
                              <span className="ml-2 flex-none rounded">
                                {sortField === 'label' ? (
                                  sortDirection === 'asc' ? (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  )
                                ) : (
                                  <svg className="h-4 w-4 text-gray-400 group-hover:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              className="group inline-flex items-center hover:text-gray-700"
                              onClick={() => handleSort('type')}
                            >
                              TYPE
                              <span className="ml-2 flex-none rounded">
                                {sortField === 'type' ? (
                                  sortDirection === 'asc' ? (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  )
                                ) : (
                                  <svg className="h-4 w-4 text-gray-400 group-hover:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              className="group inline-flex items-center hover:text-gray-700"
                              onClick={() => handleSort('performance')}
                            >
                              PERFORMANCE
                              <span className="ml-2 flex-none rounded">
                                {sortField === 'performance' ? (
                                  sortDirection === 'asc' ? (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  )
                                ) : (
                                  <svg className="h-4 w-4 text-gray-400 group-hover:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              className="group inline-flex items-center hover:text-gray-700"
                              onClick={() => handleSort('summary')}
                            >
                              SUMMARY
                              <span className="ml-2 flex-none rounded">
                                {sortField === 'summary' ? (
                                  sortDirection === 'asc' ? (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  )
                                ) : (
                                  <svg className="h-4 w-4 text-gray-400 group-hover:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button
                              className="group inline-flex items-center hover:text-gray-700"
                              onClick={() => handleSort('lastModified')}
                            >
                              LAST MODIFIED
                              <span className="ml-2 flex-none rounded">
                                {sortField === 'lastModified' ? (
                                  sortDirection === 'asc' ? (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  )
                                ) : (
                                  <svg className="h-4 w-4 text-gray-400 group-hover:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </span>
                            </button>
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ACTIONS
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {isLoading ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                              Loading flows...
                            </td>
                          </tr>
                        ) : filteredFlows.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                              {flows.length === 0 ? 'No flows found. Click "Refresh from Salesforce" to sync.' : 'No flows match your filters'}
                            </td>
                          </tr>
                        ) : (
                          filteredFlows.map((flow) => (
                            <tr 
                              key={getFlowId(flow)}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate max-w-xs">
                                {getDisplayValue(flow, 'name').length > 40 ? (
                                  <Tooltip content={getDisplayValue(flow, 'name')} position="top">
                                    <span>{getDisplayValue(flow, 'name')}</span>
                                  </Tooltip>
                                ) : (
                                  getDisplayValue(flow, 'name')
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {getDisplayValue(flow, 'processType')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {flow.performance_grade ? (
                                  <div className="flex items-center gap-2">
                                    <span className={`
                                      px-2 py-1 rounded text-sm font-medium
                                      ${flow.performance_grade === 'A' ? 'bg-green-100 text-green-800' :
                                        flow.performance_grade === 'B' ? 'bg-blue-100 text-blue-800' :
                                        flow.performance_grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                                        flow.performance_grade === 'D' ? 'bg-orange-100 text-orange-800' :
                                        flow.performance_grade === 'F' ? 'bg-gray-100 text-gray-800' :
                                        'bg-gray-100 text-gray-800'}
                                    `}>
                                      {flow.performance_grade === 'F' ? 'I' : flow.performance_grade}
                                    </span>
                                    <span className="text-sm text-gray-600">
                                      ({flow.performance_score})
                                    </span>
                                    {flow.performance_issues?.issueCount > 0 && (
                                      <span className="text-xs text-red-600">
                                        {flow.performance_issues.issueCount} issues
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const { data: { session } } = await supabase.auth.getSession();
                                        if (!session) {
                                          throw new Error('No active session');
                                        }
                                        
                                        const response = await fetch('/api/salesforce/performance-scorecard', {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${session.access_token}`
                                          },
                                          body: JSON.stringify({ flowId: flow.id })
                                        });
                                        
                                        if (!response.ok) {
                                          const errorData = await response.json();
                                          throw new Error(errorData.message || 'Failed to calculate score');
                                        }
                                        
                                        // Refresh the flow data
                                        window.location.reload();
                                      } catch (error) {
                                        console.error('Error calculating score:', error);
                                        alert(error instanceof Error ? error.message : 'Failed to calculate score');
                                      }
                                    }}
                                    className="text-sm text-blue-600 hover:underline"
                                  >
                                    Calculate Score
                                  </button>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {(() => {
                                  let status = 'new';
                                  let statusDetail = null;
                                  
                                  if (currentWorkspace) {
                                    // Check if there's a summary for this workspace
                                    const workspaceSummary = flow.sf_flow_summaries?.find((s: any) => 
                                      s.workspace_id === currentWorkspace.id
                                    );
                                    
                                    if (workspaceSummary) {
                                      status = 'completed';
                                    } else {
                                      // No summary for this workspace - it's new
                                      status = 'new';
                                    }
                                  } else {
                                    // No workspace selected - use flow's default status
                                    status = flow.app_status || 'pending';
                                  }
                                  
                                  return (
                                    <span 
                                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        status === 'completed' 
                                          ? 'bg-blue-100 text-blue-800' 
                                          : status === 'processing'
                                          ? 'bg-green-100 text-green-800'
                                          : status === 'failed'
                                          ? 'bg-red-100 text-red-800'
                                          : status === 'pending'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : status === 'new'
                                          ? 'bg-gray-100 text-gray-800'
                                          : 'bg-gray-100 text-gray-800'
                                      }`}
                                      title={statusDetail || undefined}
                                    >
                                      {status === 'completed' 
                                        ? 'Summarized' 
                                        : status === 'processing'
                                        ? 'Processing'
                                        : status === 'failed'
                                        ? 'Failed'
                                        : status === 'pending'
                                        ? 'Pending'
                                        : 'New'}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {getDisplayValue(flow, 'lastModified') 
                                  ? new Date(getDisplayValue(flow, 'lastModified')).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <a
                                  href={`/${encodeURIComponent(selectedOrgId || '')}/flow/${encodeURIComponent(getFlowId(flow))}`}
                                  onClick={(e) => {
                                    // Allow right-click and middle-click to work normally
                                    if (e.button !== 0 || e.metaKey || e.ctrlKey) {
                                      return;
                                    }
                                    e.preventDefault();
                                    handleFlowClick(getFlowId(flow));
                                  }}
                                  className="text-indigo-600 hover:text-indigo-900"
                                >
                                  View Details
                                </a>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                    </>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      
      {/* Sync Preview Modal */}
      {showSyncPreview && syncPreviewData && (
        <SyncPreviewModal
          isOpen={showSyncPreview}
          onClose={() => setShowSyncPreview(false)}
          onConfirm={handleConfirmSync}
          syncSummary={syncPreviewData.sync_summary}
          details={syncPreviewData.details}
          isSyncing={isSyncing}
        />
      )}
      
      {/* Sync Progress Indicator */}
      <SyncProgressIndicator
        isOpen={showSyncProgress}
        onClose={() => {
          setShowSyncProgress(false);
          // Reload the page to show updated flows
          window.location.reload();
        }}
        orgId={selectedOrgId || ''}
        totalFlows={syncTotalFlows}
      />
      
      {/* Reauth Modal */}
      {showReauthModal && reauthOrgId && (
        <ReauthModal
          isOpen={showReauthModal}
          onClose={() => {
            setShowReauthModal(false);
            setReauthOrgId(null);
          }}
          orgId={reauthOrgId}
          orgName={orgs.find(o => o.id === reauthOrgId)?.name || 'Salesforce Org'}
        />
      )}

      {/* Trial Export Limit Modal */}
      <TrialExportLimitModal
        isOpen={showTrialExportModal}
        onClose={() => setShowTrialExportModal(false)}
      />

      {/* Chatbot */}
      <Chatbot />
    </div>
  )
}